import { UserPlusIcon } from "@heroicons/react/24/solid";
import { Avatar } from "@moe/oss/ui/avatar";
import { Button } from "@moe/oss/ui/button";
import { Profile } from "@moe/priv/model/profile";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import React, { useState } from "react";

interface FollowCardProps {
  profile: Profile;
}

export function FollowCard({ profile }: FollowCardProps) {
  const { session } = useAppContext();
  const [isFollowing, setIsFollowing] = useState(profile.isFollowing);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();
  const { access, closeModal } = useAppContext();
  const isOwnProfile = profile.id === session?.user.id;

  const followMutation = useMutation({
    mutationFn: () => access.profile.follow({ id: profile?.id }),
    onSuccess: () => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsFollowing(true);
        setIsAnimating(false);
      }, 300);
    }
  });

  const handleFollowToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAnimating) {
      followMutation.mutate();
    }
  };

  const handleFollowCardClick = () => {
    navigate({ to: "/profile", search: { username: profile.username } });
    closeModal();
  };

  return (
    <div className="text-tx-primary group flex h-fit w-full items-center justify-between overflow-x-hidden rounded-lg p-3 font-[480] transition duration-200 ease-out hover:brightness-90 focus:outline-none">
      <button onClick={handleFollowCardClick}>
        <div className="mr-3 flex items-center space-x-5">
          <Avatar avatarURI={profile.avatar} />
          <div className="flex w-full flex-col">
            <h3 className="line-clamp-1 text-ellipsis text-left text-base font-[550]">{profile.username}</h3>
            {profile.bio && (
              <p className="text-tx-secondary line-clamp-1 text-ellipsis text-left text-sm font-[470]">{profile.bio}</p>
            )}
          </div>
        </div>
      </button>

      <div className="flex pl-8">
        <AnimatePresence>
          {!isOwnProfile && !isFollowing && (
            <motion.div
              initial={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              animate={isAnimating ? { scale: [1, 1.2, 0], opacity: [1, 1, 0] } : { scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <Button
                className="flex items-center justify-center space-x-2 rounded-3xl disabled:cursor-not-allowed"
                onClick={handleFollowToggle}
              >
                <UserPlusIcon className="text-tx-primary size-5" />
                <span className="text-tx-primary font-medium">Follow</span>
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
